/**
 * Wakes the SW BackgroundSync queue from the page context.
 *
 * iOS WebKit (as of WebKit 26 / April 2026) does not implement the
 * Background Sync API natively — Workbox falls back to listening for
 * `online` events while the SW is alive, but iOS terminates idle service
 * workers after a few minutes. The reliable wake-up on iOS is a page → SW
 * message on `visibilitychange` + `online`.
 *
 * The SW message handler in `apps/web/src/sw.ts` catches
 * `{type:'DRAIN_QUEUE'}` and calls `shoppingSyncPlugin._queue.replayRequests()`.
 *
 * Source: 04-RESEARCH.md §Pitfall 2 (iOS Background Sync absent).
 */
export function registerShoppingDrainTriggers(): () => void {
  let registered = false;

  function postDrainIfPossible(): void {
    if (typeof navigator === 'undefined') return;
    if (!navigator.onLine) return;
    const ctrl = navigator.serviceWorker?.controller;
    if (!ctrl) return;
    ctrl.postMessage({ type: 'DRAIN_QUEUE' });
  }

  function onVisibility(): void {
    if (typeof document === 'undefined') return;
    if (document.visibilityState !== 'visible') return;
    postDrainIfPossible();
  }

  function onOnline(): void {
    postDrainIfPossible();
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
    registered = true;
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('online', onOnline);
    registered = true;
  }

  return function teardown(): void {
    if (!registered) return;
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', onOnline);
    }
  };
}
